import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Mail, CheckCircle2, XCircle, Clock, Eye } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useTenant } from '@/contexts/TenantContext';
import { fetchEmailLogs, EmailLogRow } from '@/lib/api/emailLog';

const EMAIL_TYPE_LABELS: Record<string, string> = {
  tenant_invite: 'Tenant invite',
  admin_welcome: 'Admin welcome',
  super_admin_welcome: 'Super admin welcome',
  password_reset: 'Password reset',
  resend_welcome: 'Resend welcome',
  other: 'Other',
};

function StatusBadge({ status }: { status: EmailLogRow['status'] }) {
  if (status === 'sent') {
    return (
      <Badge variant="outline" className="text-green-700 border-green-300 bg-green-50">
        <CheckCircle2 className="w-3 h-3 mr-1" /> Sent
      </Badge>
    );
  }
  if (status === 'failed') {
    return (
      <Badge variant="outline" className="text-red-700 border-red-300 bg-red-50">
        <XCircle className="w-3 h-3 mr-1" /> Failed
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50">
      <Clock className="w-3 h-3 mr-1" /> Queued
    </Badge>
  );
}

export default function EmailHistory() {
  const { tenant, isMasterTenant } = useTenant();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState<EmailLogRow | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['email-log', tenant.tenant_id, search, typeFilter, statusFilter, isMasterTenant],
    queryFn: () =>
      fetchEmailLogs({
        tenant_id: isMasterTenant ? undefined : tenant.tenant_id,
        recipient_email: search || undefined,
        email_type: typeFilter,
        status: statusFilter === 'all' ? undefined : (statusFilter as 'queued' | 'sent' | 'failed'),
        limit: 100,
      }),
    enabled: !!tenant.tenant_id,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
            <Mail className="w-7 h-7" /> Email History
          </h1>
          <p className="text-muted-foreground mt-1">
            {isMasterTenant
              ? 'Every outbound email across all tenants — filter by user or type to debug delivery.'
              : 'Every invite, welcome, and password email sent to users in this tenant.'}
          </p>
        </div>
        <Button variant="outline" onClick={() => refetch()}>Refresh</Button>
      </div>

      <div className="bg-background border border-border rounded-xl p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <Input
            placeholder="Search by recipient email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1"
          />
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-full md:w-[220px]">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {Object.entries(EMAIL_TYPE_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full md:w-[160px]">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="queued">Queued</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-background border border-border rounded-xl p-12 text-center">
          <Mail className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-xl font-semibold mb-2">No emails found</h3>
          <p className="text-muted-foreground">
            Emails sent to users will appear here. Try inviting a user or resending a welcome email.
          </p>
        </div>
      ) : (
        <div className="bg-background border border-border rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr className="text-left text-sm text-muted-foreground">
                <th className="px-4 py-3 font-medium">Recipient</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Subject</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Sent at</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-b border-border last:border-0 hover:bg-muted/30">
                  <td className="px-4 py-3 text-sm font-mono">{row.recipient_email}</td>
                  <td className="px-4 py-3 text-sm">
                    {EMAIL_TYPE_LABELS[row.email_type] ?? row.email_type}
                  </td>
                  <td className="px-4 py-3 text-sm max-w-md truncate">{row.subject}</td>
                  <td className="px-4 py-3"><StatusBadge status={row.status} /></td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {new Date(row.created_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-3">
                    <Button size="sm" variant="ghost" onClick={() => setSelected(row)}>
                      <Eye className="w-4 h-4 mr-1" /> View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-3 text-xs text-muted-foreground bg-muted/30 border-t border-border">
            Showing {rows.length} of {total}
          </div>
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{selected?.subject}</DialogTitle>
            <DialogDescription>
              To <span className="font-mono">{selected?.recipient_email}</span>
              {' · '}
              {selected?.created_at && new Date(selected.created_at).toLocaleString()}
              {selected && ' · '}
              {selected && <StatusBadge status={selected.status} />}
            </DialogDescription>
          </DialogHeader>

          {selected?.status === 'failed' && selected.error_message && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800">
              <strong>Error:</strong> {selected.error_message}
            </div>
          )}

          <div className="flex-1 overflow-auto border border-border rounded-md bg-white">
            {selected?.body_html ? (
              <iframe
                title="email-preview"
                srcDoc={selected.body_html}
                className="w-full h-[60vh] border-0"
                sandbox=""
              />
            ) : (
              <div className="p-8 text-center text-muted-foreground">No body stored.</div>
            )}
          </div>

          {selected?.metadata && Object.keys(selected.metadata).length > 0 && (
            <details className="text-xs text-muted-foreground">
              <summary className="cursor-pointer">Metadata</summary>
              <pre className="mt-2 p-2 bg-muted rounded overflow-auto">
                {JSON.stringify(selected.metadata, null, 2)}
              </pre>
            </details>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
